import { isBoolean } from 'lodash';
import { Injectable, OnDestroy, Type } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { CloseModal, ModalMetadata, NewModal } from '../modal-dialog-model/modal-dialog.model';
import { ModalBase } from '../../modal-control';
import { filter, map } from 'rxjs/operators';

/**
 * Service for handling the opening and closing of the modal dialog
 */
@Injectable()
export class ModalDialogService implements OnDestroy {
  /**
   * Get the modal data stream
   *
   * @readonly
   */
  get modalData(): Observable<ModalMetadata> {
    return this._modalData.asObservable();
  }

  /**
   * Triggers when dialog window opens (true) or closes (false).
   * @returns {Observable<boolean>}
   */
  get openedOrClosed(): Observable<boolean> {
    return this._modalData.pipe(
      map(d => {
        if (d instanceof NewModal) {
          return true;
        } else if (d instanceof CloseModal) {
          return false;
        }
        return null;
      }),
      filter(b => isBoolean(b))
    );
  }

  /**
   * Modal subject
   *
   * @private
   * @type {Subject<ModalMetaData>}
   */
  private _modalData: Subject<ModalMetadata> = new Subject<ModalMetadata>();

  /**
   * Opened modal set
   *
   */
  private openedModal = new Set();

  public onClose(): Observable<ModalMetadata> {
    return this._modalData.pipe(filter(d => d instanceof CloseModal));
  }

  ngOnDestroy(): void {
    this._modalData.complete();
  }

  /**
   * Open dialog with a specified component and an optional input
   * @param component Component used as the modal.
   * @param input Input options that the component uses.
   */
  open<T extends ModalBase>(component: Type<T>, input?: T['input']): void {
    if (!this.openedModal.has(component)) {
      this.openedModal.add(component);
      this._modalData.next(new NewModal(component, input));
    }
  }

  /**
   * Close dialog
   */
  close(component, data?): void {
    this.openedModal.delete(component);
    this._modalData.next(new CloseModal(data));
  }
}
